import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import Skyline from "@/components/Skyline";
import WhyUs from "@/components/WhyUs";
import { PracticeCard } from "@/components/PracticeCard";
import { TeamGrid } from "@/components/Team";
import { Eyebrow, MaskedLine, Reveal } from "@/lib/motion";
import { FIRM, IMAGES, MANIFESTO, PRACTICE_AREAS, PROOF_POINTS } from "@/data/content";

const HERO_LINES = ["Build It.", "Run It.", "Own It.", "Protect It."];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section data-testid="hero-section" ref={ref} className="hero-glow relative overflow-hidden pt-32 lg:pt-40">
      <div className="mx-auto grid max-w-7xl gap-16 px-4 pb-20 sm:px-8 lg:grid-cols-12 lg:px-16 lg:pb-32">
        <motion.div style={{ y: textY }} className="flex flex-col justify-center lg:col-span-6">
          <Reveal delay={0.1} y={16}>
            <Eyebrow>We represent Tennessee</Eyebrow>
          </Reveal>
          <h1 data-testid="hero-title" className="mt-6 font-serif text-5xl font-light leading-[1.04] tracking-tight sm:text-6xl lg:text-7xl xl:text-8xl">
            {HERO_LINES.map((line, i) => (
              <MaskedLine key={line} delay={0.25 + i * 0.14}>
                {i === 3 ? <span className="italic text-gold">{line}</span> : line}
              </MaskedLine>
            ))}
          </h1>
          <Reveal delay={0.9}>
            <p data-testid="hero-tagline" className="mt-8 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Built in Nashville, focused on Tennessee. {FIRM.shortName} advises contractors, developers, businesses, and property owners through the disputes that come with building, operating, and owning — bringing experienced litigation counsel and genuine personal attention to every contract, construction, and property matter.
            </p>
          </Reveal>
          <Reveal delay={1.05}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-consultation-btn"
                onClick={() => window.dispatchEvent(new CustomEvent("hlt:open-booking"))}
                className="group inline-flex cursor-pointer items-center gap-3 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground transition-all duration-300 hover:brightness-110"
              >
                Book a Free Consultation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <Link
                to="/services"
                data-testid="hero-services-btn"
                className="inline-flex items-center gap-3 border border-border px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-foreground/85 transition-all duration-300 hover:border-gold hover:text-gold"
              >
                Areas of Practice
              </Link>
            </div>
          </Reveal>
        </motion.div>

        <motion.div style={{ y: imgY }} className="relative hidden lg:col-span-6 lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -left-6 -top-6 h-full w-full border border-gold/40" />
            <img
              src={IMAGES.hero}
              alt="Nashville skyline — Harrison Litigation Team"
              className="relative aspect-[4/3] w-full object-cover"
            />
          </motion.div>
        </motion.div>
      </div>

      <Skyline className="pointer-events-none absolute inset-x-0 bottom-0 hidden h-24 w-full opacity-25 sm:block lg:h-28" />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="pointer-events-none absolute bottom-6 left-1/2 hidden -translate-x-1/2 lg:block"
      >
        <ArrowDown className="h-4 w-4 animate-bounce text-gold/70" />
      </motion.div>
    </section>
  );
}

function CredibilityStrip() {
  return (
    <section data-testid="credibility-strip" className="border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <Reveal>
          <div className="grid gap-10 py-12 sm:grid-cols-3 sm:gap-8 lg:gap-14 lg:py-16">
            {PROOF_POINTS.map((p) => (
              <div key={p.title} data-testid="proof-point">
                <p className="font-serif text-xl font-medium tracking-tight text-gold">{p.title}</p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PracticePreview() {
  return (
    <section data-testid="services-section" className="py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <Reveal><Eyebrow>Areas of Practice</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight sm:text-5xl lg:text-7xl">
                Counsel for the things you <span className="italic text-gold">build, run, and own</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link to="/services" className="gold-underline pb-1 font-mono text-xs uppercase tracking-[0.2em] text-gold">
              View All Services
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {PRACTICE_AREAS.map((area, i) => (
            <PracticeCard key={area.id} area={area} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section data-testid="manifesto-section" className="border-y border-border bg-card/30 py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <Reveal><Eyebrow>The Firm — A Manifesto in Four Chapters</Eyebrow></Reveal>
        <div className="mt-12 space-y-0">
          {MANIFESTO.map((ch, i) => (
            <Reveal key={ch.num} delay={0.05}>
              <div
                data-testid={`manifesto-chapter-${i + 1}`}
                className="group grid gap-4 border-t border-border py-12 transition-colors duration-500 last:border-b hover:bg-card/60 lg:grid-cols-12 lg:gap-8 lg:py-16"
              >
                <div className="lg:col-span-2">
                  <span className="font-serif text-6xl font-light text-gold/40 transition-colors duration-500 group-hover:text-gold lg:text-7xl">
                    {ch.num}
                  </span>
                </div>
                <div className="lg:col-span-4">
                  <h3 className="font-serif text-2xl font-medium tracking-tight sm:text-3xl">{ch.title}</h3>
                </div>
                <div className="lg:col-span-6">
                  <p className="max-w-xl text-base leading-relaxed text-muted-foreground">{ch.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function TeamPreview() {
  return (
    <section data-testid="team-section" className="py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <Reveal><Eyebrow>Meet the Team</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight sm:text-5xl lg:text-7xl">
                A growing team. <span className="italic text-gold">Senior attention.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link to="/about" className="gold-underline pb-1 font-mono text-xs uppercase tracking-[0.2em] text-gold">
              About the Firm
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.25}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground">
            Led by founding attorney Jeanne Harrison — attorneys and legal professionals bringing litigation experience, practical judgment, and personal attention to every matter.
          </p>
        </Reveal>
        <div className="mt-14">
          <TeamGrid />
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="relative overflow-hidden border-t border-border">
      <img src={IMAGES.office} alt="" aria-hidden loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/70 to-background" />
      <div className="relative mx-auto max-w-7xl px-4 py-24 text-center sm:px-8 lg:px-16 lg:py-36">
        <Reveal><Eyebrow className="justify-center">Free Consultation</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mx-auto mt-6 max-w-3xl font-serif text-4xl font-light leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            Every build carries risk. <span className="italic text-gold">You don't have to manage it alone.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("hlt:open-booking"))}
              className="group inline-flex cursor-pointer items-center gap-3 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground transition-all duration-300 hover:brightness-110"
            >
              Book a Free Consultation
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              href={FIRM.phoneHref}
              className="inline-flex items-center gap-3 border border-border bg-background/60 px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] backdrop-blur-sm transition-all duration-300 hover:border-gold hover:text-gold"
            >
              Call {FIRM.phone}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <CredibilityStrip />
      <PracticePreview />
      <WhyUs />
      <TeamPreview />
      <CTA />
    </>
  );
}
